/* eslint-disable max-classes-per-file */
import { AccountCenterAgency } from './account-center-agency.model';
import { DatezPipe } from '@callrail/looky/ui';
import dayjs from 'dayjs';

export class PaymentStatus {
  protected readonly agency: AccountCenterAgency;

  constructor(agency: AccountCenterAgency) {
    this.agency = agency;
  }

  public static for(agency: AccountCenterAgency): PaymentStatus {
    switch (agency.state) {
      case 'trial':
        return new PaymentStatusTrial(agency);
      case 'active':
        return !!agency.trial_expires_at
          ? new PaymentStatusTrial(agency)
          : new PaymentStatusActive(agency);
      case 'late':
      case 'unpaid':
        return new PaymentStatusLateOrUnpaid(agency);
      case 'expired':
        return new PaymentStatusExpired(agency);
      case 'closed':
        return new PaymentStatusClosed(agency);
      case 'suspended':
        return new PaymentStatusSuspended(agency);
      default:
        return new PaymentStatus(agency);
    }
  }

  public get color(): string {
    return '';
  }

  public get icon(): string {
    return '';
  }

  public get message(): string {
    return '';
  }

  public get link(): string {
    return '';
  }

  public get link_text(): string {
    return '';
  }

  protected formatDate(date: string) {
    return new DatezPipe().transform(date, null, 'MMM D, YYYY') || '';
  }

  protected daysUntil(date: string) {
    if (!date) {
      return '0 days';
    }
    let days = dayjs(date).diff(dayjs(), 'days') || 0;
    if (isNaN(days)) {
      return '0 days';
    }
    days = Math.max(days, 0);
    return `${days} ${days === 1 ? 'day' : 'days'}`;
  }
}

class PaymentStatusTrial extends PaymentStatus {
  public get color(): string {
    return 'u-color-nail-salon';
  }

  public get icon(): string {
    return 'icon-new-feature';
  }

  public get message(): string {
    return `Trial ends in ${this.daysUntil(this.agency.trial_expires_at)}`;
  }

  public get link(): string {
    return `/settings/a/${this.agency.id}/account/billing/subscription`;
  }

  public get link_text(): string {
    return this.agency.added_cc_at
      ? `Invoices on ${this.formatDate(this.agency.next_invoice_on)}`
      : 'Add credit card';
  }
}

class PaymentStatusActive extends PaymentStatus {
  public get color(): string {
    return 'u-color-medium-green';
  }

  public get icon(): string {
    return 'icon-check';
  }

  public get message(): string {
    return `Paid ${this.agency.latest_invoice_total || ''}`;
  }

  public get link(): string {
    return `/a/${this.agency.id}/billing/static_invoice/${this.agency.latest_invoice_masked_id}`;
  }

  public get link_text(): string {
    return `${this.formatDate(this.agency.latest_invoice_created_at)} Invoice`;
  }
}

class PaymentStatusLateOrUnpaid extends PaymentStatusActive {
  public get color(): string {
    return 'u-color-pumpkin-spice-latte';
  }

  public get icon(): string {
    return 'icon-alert-error';
  }

  public get message(): string {
    return `Unpaid ${this.agency.latest_invoice_total || ''}`;
  }
}

class PaymentStatusExpired extends PaymentStatusTrial {
  public get message(): string {
    return `Trial expired on ${this.formatDate(this.agency.account_closed_on)}`;
  }

  public get link_text(): string {
    return 'Add credit card';
  }
}

class PaymentStatusClosed extends PaymentStatusLateOrUnpaid {
  public get message(): string {
    return `Closed on ${this.formatDate(this.agency.account_closed_on)}`;
  }

  public get link(): string {
    return `/settings/a/${this.agency.id}/account/billing/subscription`;
  }

  public get link_text(): string {
    return 'Reopen account';
  }
}

class PaymentStatusSuspended extends PaymentStatus {
  public get color(): string {
    return 'u-color-silver';
  }

  public get icon(): string {
    return 'icon-pause';
  }

  public get message(): string {
    return `Paused on ${this.formatDate(this.agency.account_closed_on)}`;
  }

  public get link(): string {
    return `/settings/a/${this.agency.id}/account/unpause`;
  }

  public get link_text(): string {
    return 'Resume account';
  }
}
